import { Avatar, Box, Group, Menu, Modal, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCalculator,
  IconChevronDown,
  IconEyeOff,
  IconHelp,
  IconHistory,
  IconInfoCircle,
  IconKey,
  IconLogout,
  IconReportMoney,
  IconSettings,
  IconShieldLock,
  IconWallet,
} from '@tabler/icons-react';
import { Link } from 'react-router';
import { useState } from 'react';
import { seja, type Instanca, type Uporabnik } from '../seja/seja';
import { barve } from '../tema';
import { ObrazecGesla } from './ObrazecGesla';
import { izklopiPush } from '../povprasevanja/push';
import { imaProdajo } from '../seja/paket';
import { sme, usePravice } from '../seja/pravice';

/**
 * Uporabnik v glavi (kot meniji Začni, Izpisi, Moduli in Okno v Swingu): ime in podjetje, naročnina (točke, nakazilo),
 * nastavitve (Superman),
 * zaključek dneva, izračun DMV, skrbništvo (samo skrbnik sistema), pomoč, informacije, zgodovina sprememb, sprememba
 * gesla, odjava. Med prijavo skrbnika kot uporabnik namesto gesla in odjave samo konec te prijave.
 * Na telefonu je viden samo krog z začetnicami, ime in podjetje sta v meniju.
 */
export function UporabniskiMeni({
  uporabnik,
  instanca,
  skrbnik = false,
  prijavaKot = false,
}: {
  uporabnik: Uporabnik;
  instanca: Instanca;
  skrbnik?: boolean;
  prijavaKot?: boolean;
}) {
  const [geslo, setGeslo] = useState(false);
  const pravice = usePravice();
  // nastavitve: vsaj ena skupina, ki jo vloga sme videti (glej Nastavitve)
  const nastavitve = (['NASTAVITVE', 'UPORABNIKI', 'PONUDBA', 'NAROCNINA'] as const).some((p) => sme(pravice, p));
  const ime = [uporabnik.ime, uporabnik.priimek].filter(Boolean).join(' ') || uporabnik.uporabniskoIme || '';
  const zacetnice =
    ime
      .split(/\s+/)
      .map((d) => d[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';

  return (
    <>
      <Menu position="bottom-end" width={240} shadow="md" radius="md">
        <Menu.Target>
          <UnstyledButton aria-label="Uporabniški meni" p={4} style={{ borderRadius: 10 }}>
            <Group gap="sm" wrap="nowrap">
              <Box ta="right" visibleFrom="sm" className="skrij-ob-meniju">
                <Text size="sm" fw={600} c="white" lh={1.3}>
                  {ime}
                </Text>
                <Text size="xs" lh={1.3} style={{ color: barve.besediloNaOzadjuSekundarno }}>
                  {instanca.naziv}
                </Text>
              </Box>
              <Avatar
                radius="xl"
                size={36}
                color="yellow"
                variant="filled"
                styles={{ placeholder: { color: barve.besedilo } }}
              >
                {zacetnice}
              </Avatar>
              <IconChevronDown size={16} color="white" />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Box px="sm" py={6}>
            <Text size="sm" fw={600}>
              {ime}
            </Text>
            <Text size="xs" c="dimmed">
              {instanca.naziv}
            </Text>
            {uporabnik.vloga && (
              <Text size="xs" c="dimmed">
                Vloga: {uporabnik.vloga}
              </Text>
            )}
          </Box>
          <Menu.Divider />
          <Menu.Item leftSection={<IconWallet size={16} />} component={Link} to="/narocnina">
            Naročnina
          </Menu.Item>
          <Menu.Divider />
          {nastavitve && (
            <Menu.Item leftSection={<IconSettings size={16} />} component={Link} to="/nastavitve">
              Nastavitve
            </Menu.Item>
          )}
          {sme(pravice, 'RACUNI') && (
            <Menu.Item leftSection={<IconReportMoney size={16} />} component={Link} to="/zakljucek-dneva">
              Zaključek dneva
            </Menu.Item>
          )}
          {imaProdajo(instanca.paket) && (
            <Menu.Item leftSection={<IconCalculator size={16} />} component={Link} to="/izracun-dmv">
              Izračun DMV
            </Menu.Item>
          )}
          {skrbnik && (
            <Menu.Item leftSection={<IconShieldLock size={16} />} component={Link} to="/skrbnistvo">
              Skrbništvo
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item leftSection={<IconHelp size={16} />} component={Link} to="/pomoc">
            Pomoč
          </Menu.Item>
          <Menu.Item leftSection={<IconInfoCircle size={16} />} component={Link} to="/informacije">
            Informacije
          </Menu.Item>
          <Menu.Item leftSection={<IconHistory size={16} />} component={Link} to="/spremembe">
            Zgodovina sprememb
          </Menu.Item>
          <Menu.Divider />
          {prijavaKot ? (
            <Menu.Item leftSection={<IconEyeOff size={16} />} onClick={() => void seja.koncajPrijavoKot()}>
              Končaj prijavo kot uporabnik
            </Menu.Item>
          ) : (
            <>
              <Menu.Item leftSection={<IconKey size={16} />} onClick={() => setGeslo(true)}>
                Spremeni geslo
              </Menu.Item>
              <Menu.Item
                leftSection={<IconLogout size={16} />}
                // obvestila o povpraševanjih ne smejo prihajati v brskalnik naslednjega uporabnika
                onClick={() =>
                  void izklopiPush()
                    .catch(() => undefined)
                    .finally(() => void seja.odjava())
                }
              >
                Odjava
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      <Modal opened={geslo} onClose={() => setGeslo(false)} title="Spremeni geslo" radius="lg">
        <ObrazecGesla
          oznakaStarega="Trenutno geslo"
          gumb="Spremeni geslo"
          ob={() => {
            setGeslo(false);
            notifications.show({ color: 'green', message: 'Geslo je spremenjeno.' });
          }}
        />
      </Modal>
    </>
  );
}
